import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import LockIcon from '@mui/icons-material/Lock';
import {
  HEALTH_GOAL_LABEL_KEYS,
  HEALTH_GOALS,
  isKnownHealthGoal,
} from '../../../common/utils/video-placement-utils';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvVideoHealthGoalsProps {
  goals: string[];
  label: string;
}

const OvVideoHealthGoals: FC<OvVideoHealthGoalsProps> = ({ goals, label }) => {
  const { t } = useTranslation();

  if (!goals.length) {
    return <Everyone>{t('videos.placement.everyone')}</Everyone>;
  }

  const knownGoals = HEALTH_GOALS.filter((goal) => goals.includes(goal));
  const unknownGoals = goals.filter((goal) => !isKnownHealthGoal(goal));

  return (
    <List aria-label={label}>
      {knownGoals.map((goal) => (
        <Goal key={goal}>{t(HEALTH_GOAL_LABEL_KEYS[goal])}</Goal>
      ))}
      {unknownGoals.map((goal) => (
        <li key={goal}>
          <LockedGoal>
            <LockIcon aria-hidden="true" />
            {goal}
          </LockedGoal>
          <Helper>{t('videos.healthGoals.unknownShort')}</Helper>
        </li>
      ))}
    </List>
  );
};

export default OvVideoHealthGoals;

const Everyone = styled.span`
  font-weight: bold;
`;

const List = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Goal = styled.li`
  padding: 0.125rem 0.5rem;
  border: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
  font-size: ${Variables.fontSizes.SMALL};
  white-space: nowrap;
`;

const LockedGoal = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.5rem;
  border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
  background-color: ${Colours.OV_DISABLED};
  font-size: ${Variables.fontSizes.SMALL};
  white-space: nowrap;

  .MuiSvgIcon-root {
    width: 0.75rem;
    height: 0.75rem;
  }
`;

const Helper = styled.span`
  display: block;
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
`;
